import { useEffect, useState } from 'react'
import {
  createLocation,
  deleteLocation,
  listLocations,
  updateLocation,
  type Location,
  type LocationInput,
} from '../api/locations'
import { LocationForm } from './LocationForm'

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; items: Location[] }

// The CRUD screen: the list on one side, one shared add/edit form on the other.
//
// After every successful write it reloads from the API rather than patching local
// state. Slower by one request, but the list can never drift from what the server
// actually holds — which matters more than usual here, because the store is in-memory
// per Lambda container and genuinely can change underneath you.
export function LocationManager() {
  const [state, setState] = useState<State>({ status: 'loading' })
  const [editing, setEditing] = useState<Location | null>(null)
  const [busy, setBusy] = useState(false)
  const [writeError, setWriteError] = useState<string | null>(null)

  async function load() {
    setState({ status: 'loading' })
    try {
      setState({ status: 'ready', items: await listLocations() })
    } catch (err) {
      setState({
        status: 'error',
        message: err instanceof Error ? err.message : 'Unknown error',
      })
    }
  }

  useEffect(() => {
    void load()
  }, [])

  // One handler for both create and update — which it is depends on the selection.
  async function handleSubmit(input: LocationInput) {
    setBusy(true)
    setWriteError(null)
    try {
      if (editing) {
        await updateLocation(editing.id, input)
      } else {
        await createLocation(input)
      }
      setEditing(null)
      await load()
    } catch (err) {
      setWriteError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setBusy(false)
    }
  }

  async function handleDelete(location: Location) {
    if (!window.confirm(`Delete ${location.name}? This cannot be undone.`)) return

    setBusy(true)
    setWriteError(null)
    try {
      await deleteLocation(location.id)
      // Clear the form if it was showing the row that just went.
      if (editing?.id === location.id) setEditing(null)
      await load()
    } catch (err) {
      setWriteError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className="locations">
      <LocationForm
        editing={editing}
        busy={busy}
        onSubmit={handleSubmit}
        onCancel={() => setEditing(null)}
      />

      {writeError && (
        <p className="error" role="alert">
          {writeError}
        </p>
      )}

      {state.status === 'loading' && <p className="muted">Loading locations…</p>}

      {state.status === 'error' && (
        <p className="error" role="alert">
          Couldn’t load locations: {state.message}
        </p>
      )}

      {state.status === 'ready' && state.items.length === 0 && (
        <p className="muted">No locations yet. Add one above.</p>
      )}

      {state.status === 'ready' && state.items.length > 0 && (
        <div className="locations__table-wrap">
          <table className="locations__table">
            <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Country</th>
                <th scope="col">Latitude</th>
                <th scope="col">Longitude</th>
                <th scope="col">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {state.items.map((item) => (
                <tr key={item.id} className={editing?.id === item.id ? 'is-editing' : undefined}>
                  <td>{item.name}</td>
                  <td>{item.country}</td>
                  <td className="locations__num">{item.latitude.toFixed(4)}</td>
                  <td className="locations__num">{item.longitude.toFixed(4)}</td>
                  <td className="locations__actions">
                    <button
                      type="button"
                      className="locations__btn"
                      onClick={() => setEditing(item)}
                      disabled={busy}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="locations__btn locations__btn--danger"
                      onClick={() => void handleDelete(item)}
                      disabled={busy}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
